/**
 * Dashboard Grid Component
 * CSS Grid layout with custom drag-and-drop functionality
 */

import React, { useMemo, useState, useCallback } from "react";
import { DashboardLayout, DashboardWidget } from "../types";

interface DashboardGridProps {
  layout: DashboardLayout;
  widgets: Map<string, React.ReactNode>; // Map of instanceId -> widget component
  widgetTitles: Map<string, string>; // Map of instanceId -> widget title
  onLayoutChange: (newLayout: DashboardLayout) => void;
  onHideWidget: (instanceId: string) => void;
}

export function DashboardGrid({
  layout,
  widgets,
  widgetTitles,
  onLayoutChange,
  onHideWidget,
}: DashboardGridProps) {
  // Get current column count
  const currentGridColumns = layout.cols.lg;
  
  // Drag state
  const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
  const [dragOverWidgetId, setDragOverWidgetId] = useState<string | null>(null);
  
  // Filter visible widgets - IMPORTANT: Order is preserved by array index
  // The order of widgets in layout.widgets array determines display order
  // Array index = display order (saved to localStorage)
  const visibleWidgets = useMemo(
    () => {
      // Filter preserves array order, so widgets will render in the same order
      // they appear in layout.widgets array
      return layout.widgets.filter((w) => w.visible);
    },
    [layout.widgets]
  );

  // Determine CSS Grid columns class based on column count
  const gridColsClass = useMemo(() => {
    switch (currentGridColumns) {
      case 1:
        return "grid-cols-1";
      case 2:
        return "grid-cols-1 md:grid-cols-2";
      case 3:
        return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
      default:
        return "grid-cols-1 md:grid-cols-2";
    }
  }, [currentGridColumns]);

  // Handle drag start
  const handleDragStart = useCallback((e: React.DragEvent, widgetId: string) => {
    setDraggedWidgetId(widgetId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", widgetId);
    
    // Create a custom drag image (optional - for better UX)
    const dragElement = e.currentTarget.closest('[draggable]') as HTMLElement;
    if (dragElement) {
      dragElement.style.opacity = "0.5";
    }
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback((e: React.DragEvent) => {
    // Reset opacity
    const dragElement = e.currentTarget.closest('[draggable]') as HTMLElement;
    if (dragElement) {
      dragElement.style.opacity = "1";
    }
    
    setDraggedWidgetId(null);
    setDragOverWidgetId(null);
  }, []);

  // Handle drag over
  const handleDragOver = useCallback((e: React.DragEvent, widgetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    
    if (draggedWidgetId && draggedWidgetId !== widgetId) {
      setDragOverWidgetId(widgetId);
    }
  }, [draggedWidgetId]);

  // Handle drag leave
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear dragOver if we're leaving the grid area, not just moving between children
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!e.currentTarget.contains(relatedTarget)) {
      setDragOverWidgetId(null);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback((e: React.DragEvent, dropTargetId: string) => {
    e.preventDefault();
    
    if (!draggedWidgetId || draggedWidgetId === dropTargetId) {
      setDraggedWidgetId(null);
      setDragOverWidgetId(null);
      return;
    }

    // Find indices in the full widgets array (including hidden widgets)
    // Array index represents the display order - this is what we save to storage
    const draggedIndex = layout.widgets.findIndex(w => w.instanceId === draggedWidgetId);
    const dropIndex = layout.widgets.findIndex(w => w.instanceId === dropTargetId);

    if (draggedIndex === -1 || dropIndex === -1) {
      setDraggedWidgetId(null);
      setDragOverWidgetId(null);
      return;
    }

    // Reorder widgets in the full array
    // This preserves the order of ALL widgets (visible and hidden)
    // The array index represents the display order - this is saved to local storage
    const reorderedWidgets = [...layout.widgets];
    const [draggedWidget] = reorderedWidgets.splice(draggedIndex, 1);
    reorderedWidgets.splice(dropIndex, 0, draggedWidget);

    // Create updated layout with new widget order
    // Order is stored by array index - each widget's position in the array
    // determines its display order when visible
    const newLayout: DashboardLayout = {
      ...layout,
      widgets: reorderedWidgets, // Order stored by array index
      // cols: already preserved from layout (column layout)
      // Each widget.visible: already preserved (hidden charts)
    };

    onLayoutChange(newLayout);
    setDraggedWidgetId(null);
    setDragOverWidgetId(null);
  }, [draggedWidgetId, layout, onLayoutChange]);

  // Render widget content
  const renderWidget = (widget: DashboardWidget, index: number) => {
    const widgetContent = widgets.get(widget.instanceId);
    const isDragging = draggedWidgetId === widget.instanceId;
    const isDragOver = dragOverWidgetId === widget.instanceId;

    if (!widgetContent) {
      return (
        <div key={widget.instanceId} className="flex items-center justify-center h-full min-h-[400px]">
          <p className="text-sm text-muted-foreground">Widget not found</p>
        </div>
      );
    }

    // All widgets use HOC pattern (stored as object with type: 'hoc')
    if (widgetContent && typeof widgetContent === 'object' && 'type' in widgetContent && (widgetContent as any).type === 'hoc') {
      const hocData = widgetContent as any;
      const WrappedComponent = hocData.component;
      
      return (
        <div
          key={widget.instanceId}
          onDragOver={(e) => handleDragOver(e, widget.instanceId)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, widget.instanceId)}
          className={`
            transition-all duration-200
            min-h-[400px] h-full w-full
            ${isDragOver ? 'ring-2 ring-accent ring-offset-2' : ''}
          `}
        >
          <WrappedComponent
            {...hocData.props}
            widget={widget}
            onHide={onHideWidget}
            onDragStart={(e: React.DragEvent) => handleDragStart(e, widget.instanceId)}
            onDragEnd={handleDragEnd}
            isDragging={isDragging}
          />
        </div>
      );
    }

    // Fallback (should not happen with HOC pattern)
    return (
      <div
        key={widget.instanceId}
        className="min-h-[400px] h-full w-full flex items-center justify-center"
      >
        <p className="text-sm text-muted-foreground">Widget rendering error</p>
      </div>
    );
  };

  // Render widgets in the exact order they appear in layout.widgets
  // This order is saved to localStorage and restored on page reload
  return (
    <div className={`grid ${gridColsClass} gap-2 sm:gap-3 md:gap-4 lg:gap-5 w-full`}>
      {visibleWidgets.map((widget, index) => {
        // Render widgets in the order they appear in the array
        // This order is preserved from localStorage
        return renderWidget(widget, index);
      })}
    </div>
  );
}
